import { Stack, Card, Box } from '@mui/material';
import { Typography, Button, colors } from '@sweep-io/sweep-design';
import IssuesTableImage from './issuesTableImage.svg?react';
import { Issue } from './issuesTypes';
import { ActiveScan, ScanStatus } from '@server/issues';
import useAgentsHome from '../aiAgentCenter/useAgentsHome';
import { useSelector } from 'react-redux';
import { selectIsIssuesPanelOpen } from '../aiAgentCenter/agentsHomeReducer';
import ScanningIndicator from './ScanningIndicator';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import { ISSUES_CARD_NARROW_QUERY } from '../aiAgentCenter/aiAgentsConsts';

interface IssuesCardProps {
  issues?: Issue[];
  objectLabel: string;
  isLoading: boolean;
  activeScan?: ActiveScan;
}

const IssuesCard: React.FC<IssuesCardProps> = ({ issues, objectLabel, isLoading, activeScan }) => {
  const { isExpanded: isPanelExpanded, setIsExpanded, openIssuesPanel } = useAgentsHome();
  const isIssuesPanelOpen = useSelector(selectIsIssuesPanelOpen);
  const issuesCount = issues?.length;
  const isScanInProgress = activeScan && activeScan?.status !== ScanStatus.DONE;
  const summaryText =
    isScanInProgress && !issuesCount
      ? undefined
      : `${issuesCount || 'No'} issues found ${objectLabel ? `in the ${objectLabel} object` : ''}`;

  const handleCardClick = () => {
    if (isIssuesPanelOpen) {
      return;
    }
    // If in collapsed mode, expand first, then open panel
    if (!isPanelExpanded) {
      setIsExpanded({ isExpanded: true });
    }
    openIssuesPanel();
  };

  return (
    <Card
      elevation={0}
      sx={{
        containerType: 'inline-size',
        pointerEvents: isLoading ? 'none' : 'initial',
        width: '100%',
        borderRadius: '8px',
        padding: isPanelExpanded ? '16px 24px' : '16px',
        display: 'flex',
        justifyContent: 'space-between',
        position: 'relative',
        height: isPanelExpanded ? '80px' : '129px',
        cursor: 'pointer',
        transition: 'all 0.3s ease-in-out',
        '.image-container': {
          transform: 'rotateZ(0)',
          transition: 'transform 0.2s ease-in-out',
          bottom: '-4px',
          [ISSUES_CARD_NARROW_QUERY]: {
            display: 'none',
          },
        },
        '&:hover .image-container': {
          transform: 'rotateZ(2deg)',
        },
      }}
      onClick={handleCardClick}
    >
      {isLoading && (
        <Stack justifyContent={isPanelExpanded ? 'center' : 'flex-start'}>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="body" color={colors.grey[700]}>
              Scanning for issues
            </Typography>
            <CenteredCircularProgress size={10} />
          </Stack>
        </Stack>
      )}
      {!isLoading && (
        <Stack gap={0.5} justifyContent="center">
          {summaryText && (
            <Typography variant="body" color={colors.grey[700]}>
              {summaryText}
            </Typography>
          )}
          {isScanInProgress ? (
            <ScanningIndicator />
          ) : (
            <Button variant="flat-blue" endIconName="ChevronRight">
              View details
            </Button>
          )}
        </Stack>
      )}
      <Box
        className="image-container"
        sx={{
          position: 'absolute',
          bottom: 0,
          right: isPanelExpanded ? '30px' : '16px',
          height: '61px',
          svg: {
            height: '61px',
          },
        }}
      >
        <IssuesTableImage />
      </Box>
    </Card>
  );
};

export default IssuesCard;
